/**
 * PerformanceSnapshot - compact runtime performance report for play mode.
 * Designed for JSON export (~2–8 KB) suitable for agents and quick diagnosis.
 */

import * as THREE from 'three';

import { Actor } from '../actors/Actor.js';
import { InfoActor } from '../actors/InfoActor.js';
import { PrimitiveComponent } from '../components/PrimitiveComponent.js';
import { SceneComponent } from '../components/SceneComponent.js';

import { ClassRegistry } from './ClassRegistry.js';

import type { PlayState } from '../game/Playable.js';
import type { World } from '../game/World.js';
import type { PhysicsPerformanceStats } from '../physics/PhysicsEngine.js';
import type { IGenesysRenderer } from '../render/IGenesysRenderer.js';

const SCHEMA_VERSION = 1 as const;
const PROFILER_SAMPLE_WINDOW = 120;
const TOP_CLASS_COUNT = 15;

const THRESHOLDS = {
  frameBudgetPercent: 50,
  drawCalls: 500,
  triangles: 1_000_000,
  tickStormMs: 5,
  tickStormActorCount: 100,
  physicsStepMs: 8,
  physicsBodies: 200,
  collisionEventsPerFrame: 500,
  netBytesPerSecond: 100_000,
  idleTickerRatio: 0.2,
  gpuMemoryBytes: 512 * 1024 * 1024,
  gpuMemoryCriticalBytes: 1024 * 1024 * 1024,
  textureMemoryBytes: 256 * 1024 * 1024,
  programCount: 200,
} as const;

export type PerformanceIssueSeverity = 'info' | 'warning' | 'critical';
export type PerformanceIssueCategory = 'timing' | 'render' | 'simulation' | 'physics' | 'network' | 'memory';

export interface PerformanceIssue {
  id: string;
  severity: PerformanceIssueSeverity;
  category: PerformanceIssueCategory;
  message: string;
  suggestion: string;
}

export interface ProfilerScopeStats {
  label: string;
  samples: number;
  /** Inclusive (total) average time in ms — includes child scopes. */
  avg: number;
  min: number;
  max: number;
  p95: number;
  /** Exclusive (self) average time in ms — time in this scope minus child scopes. */
  selfAvg?: number;
  selfMin?: number;
  selfMax?: number;
  selfP95?: number;
  /** Average number of times this scope is entered per frame. */
  callsPerFrame?: number;
}

/** Raw shape from THREE.ProfilerService.exportJSON() — includes fields we strip from output. */
interface RawProfilerScopeStats extends ProfilerScopeStats {
  frameBudget?: number;
  selfFrameBudget?: number;
  /** Uncapped invocation count from ProfilerService, used to derive callsPerFrame. */
  totalInvocations?: number;
}

function sanitizeScopeStats(raw: RawProfilerScopeStats): ProfilerScopeStats {
  return {
    label: raw.label,
    samples: raw.samples,
    avg: raw.avg,
    min: raw.min,
    max: raw.max,
    p95: raw.p95,
    selfAvg: raw.selfAvg,
    selfMin: raw.selfMin,
    selfMax: raw.selfMax,
    selfP95: raw.selfP95,
  };
}

export interface PerformanceSnapshotMeta {
  gameId?: string;
  mapName?: string;
  renderer: 'webgl' | 'webgpu' | 'unknown';
  playState: PlayState;
  fps?: number;
}

export interface PerformanceSnapshotRender {
  drawCalls: number;
  triangles: number;
  points: number;
  lines: number;
  geometries: number;
  textures: number;
  programs?: number;
}

export interface PerformanceSnapshotWorld {
  actorCount: number;
  actorsByClass: { className: string; count: number }[];
}

export interface GpuMemoryBucket {
  count: number;
  sizeBytes?: number;
}

export interface PerformanceSnapshotGpuMemory {
  totalBytes?: number;
  attributes?: GpuMemoryBucket;
  geometries: GpuMemoryBucket;
  indexAttributes?: GpuMemoryBucket;
  indirectStorageAttributes?: GpuMemoryBucket;
  programs: GpuMemoryBucket;
  readbackBuffers?: GpuMemoryBucket;
  renderTargets?: GpuMemoryBucket;
  storageAttributes?: GpuMemoryBucket;
  textures: GpuMemoryBucket;
}

export interface PerformanceSnapshotMemory {
  jsHeapUsedMB?: number;
  gpu?: PerformanceSnapshotGpuMemory;
}

/**
 * Aggregate statistics derived from per-frame timing entries (e.g. WebGPU Inspector
 * "frame N" labels). These are separated from function scopes to avoid polluting the
 * scopes table with one-sample-per-frame entries.
 */
export interface FrameTimingStats {
  /** Total number of frame samples observed. */
  frameCount: number;
  avgMs: number;
  minMs: number;
  maxMs: number;
  p95Ms: number;
  /** Number of frames that exceeded the 60 fps budget (16.67 ms). */
  slowFrames: number;
}

export interface PerformanceSnapshotTiming {
  profiler: {
    enabled: boolean;
    profile: 'full' | 'stats';
    sampleWindow: number;
  };
  /** Aggregate frame-timing stats (derived from per-frame "frame N" profiler labels). */
  frameStats?: FrameTimingStats;
  /** Function/method scope stats — per-label avg, p95, min, max, self time. */
  scopes: ProfilerScopeStats[];
  frameTimeMs?: number;
}

export interface PerformanceSnapshotSimulation {
  physics?: PhysicsPerformanceStats;
  timers?: { activeTimers: number };
  tweens?: { activeTweens: number };
  tickAudit?: {
    actorsTicking: number;
    componentsWithTickOverride: { className: string; count: number }[];
  };
  idleTickerCount?: number;
}

export interface PerformanceNetworkSnapshot {
  numLocalObjects: number;
  numRemoteObjects: number;
  numLocalUpdatingObjects: number;
  numRemoteUpdatingObjects: number;
  numClientsInSession: number;
  bytesSentPerSecond: number;
  bytesReceivedPerSecond: number;
  numActorsReplicated: number;
  numIncomingRPCs: number;
  numOutgoingRPCs: number;
  serverRttMs: number | null;
}

export interface PerformanceSnapshotV1 {
  schemaVersion: typeof SCHEMA_VERSION;
  capturedAt: string;
  meta: PerformanceSnapshotMeta;
  render: PerformanceSnapshotRender;
  world: PerformanceSnapshotWorld;
  memory: PerformanceSnapshotMemory;
  timing?: PerformanceSnapshotTiming;
  simulation?: PerformanceSnapshotSimulation;
  network?: PerformanceNetworkSnapshot;
  issues: PerformanceIssue[];
}

export interface PerformanceSnapshotOptions {
  gameId?: string;
  mapName?: string;
  fps?: number;
}

/** WebGPU-style memory info from renderer.info.memory. */
interface WebGpuMemoryInfo {
  geometries: number;
  textures: number;
  attributes: number;
  indexAttributes: number;
  storageAttributes: number;
  indirectStorageAttributes: number;
  readbackBuffers: number;
  programs: number;
  renderTargets: number;
  total: number;
  texturesSize: number;
  attributesSize: number;
  indexAttributesSize: number;
  storageAttributesSize: number;
  indirectStorageAttributesSize: number;
  readbackBuffersSize: number;
  programsSize: number;
}

function gpuBucket(count: number, sizeBytes?: number): GpuMemoryBucket {
  return sizeBytes !== undefined ? { count, sizeBytes } : { count };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function readGpuMemoryStats(renderer: IGenesysRenderer): PerformanceSnapshotGpuMemory | undefined {
  const webgpu = renderer.asWebGPU();
  if (webgpu) {
    const m = webgpu.info.memory as WebGpuMemoryInfo;
    return {
      totalBytes: m.total,
      attributes: gpuBucket(m.attributes, m.attributesSize),
      geometries: { count: m.geometries },
      indexAttributes: gpuBucket(m.indexAttributes, m.indexAttributesSize),
      indirectStorageAttributes: gpuBucket(m.indirectStorageAttributes, m.indirectStorageAttributesSize),
      programs: gpuBucket(m.programs, m.programsSize),
      readbackBuffers: gpuBucket(m.readbackBuffers, m.readbackBuffersSize),
      renderTargets: { count: m.renderTargets },
      storageAttributes: gpuBucket(m.storageAttributes, m.storageAttributesSize),
      textures: gpuBucket(m.textures, m.texturesSize),
    };
  }

  const webgl = renderer.asWebGL();
  if (webgl) {
    const m = webgl.info.memory;
    const programCount = webgl.info.programs?.length ?? 0;
    return {
      geometries: { count: m.geometries },
      textures: { count: m.textures },
      programs: { count: programCount },
    };
  }

  return undefined;
}

function readRenderStats(renderer: IGenesysRenderer): PerformanceSnapshotRender {
  const native = renderer.asWebGL() ?? renderer.asWebGPU();
  if (!native) {
    return {
      drawCalls: 0,
      triangles: 0,
      points: 0,
      lines: 0,
      geometries: 0,
      textures: 0,
    };
  }

  const isWebGL = renderer.asWebGL() !== null;
  const renderInfo = native.info.render;
  const memoryInfo = native.info.memory;

  return {
    drawCalls: isWebGL ? renderInfo.calls : (renderInfo as { drawCalls?: number }).drawCalls ?? 0,
    triangles: renderInfo.triangles,
    points: renderInfo.points,
    lines: renderInfo.lines,
    geometries: memoryInfo.geometries,
    textures: memoryInfo.textures,
    programs: 'programs' in memoryInfo ? memoryInfo.programs : undefined,
  };
}

function collectActorsByClass(actors: Actor[]): { className: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const actor of actors) {
    const className = ClassRegistry.getClassOrConstructorName(actor);
    counts.set(className, (counts.get(className) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([className, count]) => ({ className, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, TOP_CLASS_COUNT);
}

function componentHasTickOverride(component: SceneComponent): boolean {
  const proto = Object.getPrototypeOf(component) as SceneComponent;
  return proto.tickPrePhysics !== SceneComponent.prototype.tickPrePhysics
    || proto.tickPostPhysics !== SceneComponent.prototype.tickPostPhysics;
}

function actorHasTickOverride(actor: Actor): boolean {
  const proto = Object.getPrototypeOf(actor) as Actor;
  return proto.tickPrePhysics !== Actor.prototype.tickPrePhysics
    || proto.tickPostPhysics !== Actor.prototype.tickPostPhysics;
}

function actorHasSimulationComponent(actor: Actor): boolean {
  for (const component of actor.getComponents(SceneComponent)) {
    const className = ClassRegistry.getClassOrConstructorName(component);
    if (className.includes('Movement') || className.includes('BehaviorTree')) {
      return true;
    }
    if (componentHasTickOverride(component)) {
      return true;
    }
  }
  return false;
}

function actorHasPhysics(actor: Actor): boolean {
  const physics = actor.getPhysicsEngine();
  if (!physics) {
    return false;
  }
  for (const component of actor.getComponents(PrimitiveComponent)) {
    if (physics.getComponentState(component) !== null) {
      return true;
    }
  }
  return false;
}

function isIdleTicker(actor: Actor): boolean {
  if (actor instanceof InfoActor) {
    return true;
  }
  if (actorHasTickOverride(actor)) {
    return false;
  }
  if (actorHasPhysics(actor)) {
    return false;
  }
  if (actorHasSimulationComponent(actor)) {
    return false;
  }
  return true;
}

function collectTickAudit(actors: Actor[]): PerformanceSnapshotSimulation['tickAudit'] {
  const overrideCounts = new Map<string, number>();

  for (const actor of actors) {
    for (const component of actor.getComponents(SceneComponent)) {
      if (componentHasTickOverride(component)) {
        const className = ClassRegistry.getClassOrConstructorName(component);
        overrideCounts.set(className, (overrideCounts.get(className) ?? 0) + 1);
      }
    }
  }

  return {
    actorsTicking: actors.length,
    componentsWithTickOverride: [...overrideCounts.entries()]
      .map(([className, count]) => ({ className, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, TOP_CLASS_COUNT),
  };
}

const MAX_SCOPES = 100;
/** Matches per-frame unique labels emitted by the Three.js WebGPU Inspector ("frame 0", "frame 1107", etc.) */
const FRAME_LABEL_RE = /^frame \d+$/;
/** Matches labels with a numeric suffix like "renderObjects (291)" — captured base used for aggregation. */
const SCOPE_GROUP_RE = /^(.+?) \(\d+\)$/;

function normalizeScopeLabel(label: string): string {
  return SCOPE_GROUP_RE.exec(label)?.[1] ?? label;
}

/**
 * Merges scopes that share a normalized label (e.g. "renderObjects (291)" and
 * "renderObjects (262)") into a single entry. Averages are sample-weighted; min/max
 * take extremes; p95 is approximated by the group max since raw samples are unavailable.
 */
function mergeScopeStats(group: ProfilerScopeStats[]): ProfilerScopeStats {
  if (group.length === 1) {
    return { ...group[0], label: normalizeScopeLabel(group[0].label) };
  }
  const totalSamples = group.reduce((sum, s) => sum + s.samples, 0) || group.length;
  const weightedAvg = (pick: (s: ProfilerScopeStats) => number | undefined): number | undefined => {
    let weighted = 0;
    let weight = 0;
    for (const s of group) {
      const value = pick(s);
      if (value !== undefined) {
        weighted += value * s.samples;
        weight += s.samples;
      }
    }
    return weight > 0 ? weighted / weight : undefined;
  };

  const hasSelf = group.some(s => s.selfAvg !== undefined);
  const hasCpf = group.some(s => s.callsPerFrame !== undefined);

  return {
    label: normalizeScopeLabel(group[0].label),
    samples: totalSamples,
    avg: weightedAvg(s => s.avg) ?? 0,
    min: Math.min(...group.map(s => s.min)),
    max: Math.max(...group.map(s => s.max)),
    p95: Math.max(...group.map(s => s.p95)),
    selfAvg: hasSelf ? weightedAvg(s => s.selfAvg) : undefined,
    selfMin: hasSelf ? Math.min(...group.flatMap(s => s.selfMin !== undefined ? [s.selfMin] : [])) : undefined,
    selfMax: hasSelf ? Math.max(...group.flatMap(s => s.selfMax !== undefined ? [s.selfMax] : [])) : undefined,
    selfP95: hasSelf ? Math.max(...group.flatMap(s => s.selfP95 !== undefined ? [s.selfP95] : [])) : undefined,
    // Sum calls-per-frame across merged scopes (each instance contributes independently).
    callsPerFrame: hasCpf ? group.reduce((sum, s) => sum + (s.callsPerFrame ?? 0), 0) : undefined,
  };
}

function aggregateScopes(functionScopes: ProfilerScopeStats[]): ProfilerScopeStats[] {
  const groups = new Map<string, ProfilerScopeStats[]>();
  for (const scope of functionScopes) {
    const key = normalizeScopeLabel(scope.label);
    const existing = groups.get(key);
    if (existing) {
      existing.push(scope);
    } else {
      groups.set(key, [scope]);
    }
  }

  return [...groups.values()].map(mergeScopeStats);
}

interface ProfilerSplit {
  scopes: ProfilerScopeStats[];
  frameStats: FrameTimingStats | undefined;
}

function splitProfilerStats(): ProfilerSplit {
  const all = THREE.ProfilerService.exportJSON() as RawProfilerScopeStats[];

  const frameSamples: number[] = [];
  const rawFunctionScopes: RawProfilerScopeStats[] = [];

  for (const s of all) {
    if (FRAME_LABEL_RE.test(s.label)) {
      // Each "frame N" entry has samples=1; collect the single value as a frame-time sample.
      frameSamples.push(s.avg);
    } else {
      rawFunctionScopes.push(s);
    }
  }

  let frameStats: FrameTimingStats | undefined;
  if (frameSamples.length > 0) {
    const sorted = [...frameSamples].sort((a, b) => a - b);
    const avg = sorted.reduce((a, b) => a + b, 0) / sorted.length;
    const budget = 1000 / 60;
    frameStats = {
      frameCount: sorted.length,
      avgMs: avg,
      minMs: sorted[0],
      maxMs: sorted[sorted.length - 1],
      p95Ms: sorted[Math.floor(sorted.length * 0.95)],
      slowFrames: frameSamples.filter(v => v > budget).length,
    };
  }

  const frameCount = frameStats?.frameCount ?? 0;
  const functionScopes: ProfilerScopeStats[] = rawFunctionScopes.map(s => ({
    ...sanitizeScopeStats(s),
    callsPerFrame: frameCount > 0 && s.totalInvocations !== undefined
      ? s.totalInvocations / frameCount
      : undefined,
  }));

  const scopes = aggregateScopes(functionScopes)
    .sort((a, b) => b.avg - a.avg)
    .slice(0, MAX_SCOPES);

  return { scopes, frameStats };
}

function estimateFrameTimeMs(scopes: ProfilerScopeStats[], frameStats: FrameTimingStats | undefined): number | undefined {
  if (frameStats) {
    return frameStats.avgMs;
  }
  const gameLoopTick = scopes.find(s => s.label === 'BaseGameLoop.tick');
  if (gameLoopTick) {
    return gameLoopTick.avg;
  }
  const worldTick = scopes.find(s => s.label === 'World.tick');
  if (worldTick) {
    return worldTick.avg;
  }
  return undefined;
}

function collectNetworkSnapshot(world: World): PerformanceNetworkSnapshot | undefined {
  const netStats = world.netWorld.getNetStats();
  const hasActivity = netStats.numLocalObjects > 0
    || netStats.numRemoteObjects > 0
    || netStats.bytesSentPerSecond > 0
    || netStats.bytesReceivedPerSecond > 0;

  if (!hasActivity) {
    return undefined;
  }

  return {
    numLocalObjects: netStats.numLocalObjects,
    numRemoteObjects: netStats.numRemoteObjects,
    numLocalUpdatingObjects: netStats.numLocalUpdatingObjects,
    numRemoteUpdatingObjects: netStats.numRemoteUpdatingObjects,
    numClientsInSession: netStats.numClientsInSession,
    bytesSentPerSecond: netStats.bytesSentPerSecond,
    bytesReceivedPerSecond: netStats.bytesReceivedPerSecond,
    numActorsReplicated: netStats.numActorsReplicated,
    numIncomingRPCs: netStats.numIncomingRPCs,
    numOutgoingRPCs: netStats.numOutgoingRPCs,
    serverRttMs: netStats.serverRttMs,
  };
}

function findScope(scopes: ProfilerScopeStats[], label: string): ProfilerScopeStats | undefined {
  return scopes.find(s => s.label === label || s.label.endsWith(`.${label}`) || s.label.includes(label));
}

function buildIssues(snapshot: Omit<PerformanceSnapshotV1, 'issues'>): PerformanceIssue[] {
  const issues: PerformanceIssue[] = [];

  if (snapshot.render.drawCalls > THRESHOLDS.drawCalls) {
    issues.push({
      id: 'high_draw_calls',
      severity: 'warning',
      category: 'render',
      message: `Draw calls (${snapshot.render.drawCalls}) exceed threshold (${THRESHOLDS.drawCalls})`,
      suggestion: 'Batch meshes via InstancedMesh or merge geometries to reduce draw calls.',
    });
  }

  if (snapshot.render.triangles > THRESHOLDS.triangles) {
    issues.push({
      id: 'high_triangle_count',
      severity: 'warning',
      category: 'render',
      message: `Triangles (${snapshot.render.triangles}) exceed threshold (${THRESHOLDS.triangles})`,
      suggestion: 'Use LOD, culling, or simplify meshes to reduce triangle count.',
    });
  }

  const scopes = snapshot.timing?.scopes ?? [];
  const frameStats = snapshot.timing?.frameStats;

  // Frame timing issue — from aggregated frameStats (preferred) or from function scopes.
  if (frameStats) {
    const budget = 1000 / 60;
    const slowRatio = frameStats.slowFrames / frameStats.frameCount;
    if (frameStats.avgMs > budget || slowRatio > 0.1) {
      const severity = frameStats.avgMs > budget * 2 || slowRatio > 0.3 ? 'critical' : 'warning';
      issues.push({
        id: 'frame_budget_pressure',
        severity,
        category: 'timing',
        message: `Frame time avg ${frameStats.avgMs.toFixed(1)} ms (p95: ${frameStats.p95Ms.toFixed(1)} ms, max: ${frameStats.maxMs.toFixed(1)} ms). ${frameStats.slowFrames}/${frameStats.frameCount} frames exceeded 16.7 ms budget.`,
        suggestion: 'Check exclusive (self) time in the scope table to find where actual work happens. Run perf.snapshot 5 for aggregated function-level data.',
      });
    }
  } else {
    // Fallback: derive from function scopes when no frame-level data is available.
    const budget = 1000 / 60;
    const overBudgetMs = budget * (THRESHOLDS.frameBudgetPercent / 100);
    const criticalMs = budget * 0.8;
    const overBudgetScopes = scopes.filter(s => s.avg > overBudgetMs);
    if (overBudgetScopes.length > 0) {
      const worst = overBudgetScopes.slice(0, 5);
      const hasCritical = worst.some(s => s.avg > criticalMs);
      const worstDesc = worst
        .map(s => {
          const self = s.selfAvg !== undefined ? `, self ${s.selfAvg.toFixed(1)} ms` : '';
          return `${s.label} (avg ${s.avg.toFixed(1)} ms${self})`;
        })
        .join('; ');
      issues.push({
        id: 'frame_budget_pressure',
        severity: hasCritical ? 'critical' : 'warning',
        category: 'timing',
        message: `${overBudgetScopes.length} scope(s) exceed ${overBudgetMs.toFixed(1)} ms avg. Worst: ${worstDesc}`,
        suggestion: 'Check exclusive (self) time to find where actual work happens vs. orchestration.',
      });
    }
  }

  const tickScope = findScope(scopes, 'tickActorsPrePhysics');
  if (tickScope
    && tickScope.avg > THRESHOLDS.tickStormMs
    && snapshot.world.actorCount > THRESHOLDS.tickStormActorCount) {
    issues.push({
      id: 'tick_storm',
      severity: 'warning',
      category: 'simulation',
      message: `Actor pre-physics tick (${tickScope.avg.toFixed(2)} ms) is high with ${snapshot.world.actorCount} actors`,
      suggestion: 'Reduce per-actor tick work, disable unnecessary tickers, or batch updates.',
    });
  }

  const physicsScope = findScope(scopes, 'world.step');
  const bodyCount = snapshot.simulation?.physics?.bodies ?? 0;
  if (physicsScope && physicsScope.avg > THRESHOLDS.physicsStepMs) {
    issues.push({
      id: 'physics_step_heavy',
      severity: 'warning',
      category: 'physics',
      message: `Physics step (${physicsScope.avg.toFixed(2)} ms) exceeds threshold (${THRESHOLDS.physicsStepMs} ms)`,
      suggestion: 'Reduce dynamic bodies, simplify colliders, or lower physics update rate.',
    });
  }
  if (bodyCount > THRESHOLDS.physicsBodies) {
    issues.push({
      id: 'physics_body_count',
      severity: 'info',
      category: 'physics',
      message: `Physics bodies (${bodyCount}) exceed threshold (${THRESHOLDS.physicsBodies})`,
      suggestion: 'Review static vs dynamic body usage and merge colliders where possible.',
    });
  }

  const collisionEvents = snapshot.simulation?.physics?.collisionEventsLastFrame ?? 0;
  if (collisionEvents > THRESHOLDS.collisionEventsPerFrame) {
    issues.push({
      id: 'collision_event_storm',
      severity: 'warning',
      category: 'physics',
      message: `High collision event count (${collisionEvents}/frame) exceeds threshold (${THRESHOLDS.collisionEventsPerFrame})`,
      suggestion: 'Reduce overlapping colliders, use collision layers/filters, or switch to sensors for non-physical overlap detection.',
    });
  }

  const idleCount = snapshot.simulation?.idleTickerCount ?? 0;
  if (snapshot.world.actorCount > 0 && idleCount / snapshot.world.actorCount > THRESHOLDS.idleTickerRatio) {
    issues.push({
      id: 'idle_tick_waste',
      severity: 'info',
      category: 'simulation',
      message: `${idleCount} of ${snapshot.world.actorCount} actors tick without simulation work (${((idleCount / snapshot.world.actorCount) * 100).toFixed(0)}%)`,
      suggestion: 'Consider InfoActor for state-only objects or skip ticking for passive actors.',
    });
  }

  if (snapshot.network && snapshot.network.bytesSentPerSecond > THRESHOLDS.netBytesPerSecond) {
    issues.push({
      id: 'net_saturation',
      severity: 'warning',
      category: 'network',
      message: `Network send rate (${snapshot.network.bytesSentPerSecond} B/s) is high`,
      suggestion: 'Reduce replication frequency, filter irrelevant objects, or compress RPC payloads.',
    });
  }

  const gpu = snapshot.memory.gpu;
  if (gpu?.totalBytes !== undefined) {
    if (gpu.totalBytes > THRESHOLDS.gpuMemoryCriticalBytes) {
      issues.push({
        id: 'high_gpu_memory_critical',
        severity: 'critical',
        category: 'memory',
        message: `GPU memory (${formatBytes(gpu.totalBytes)}) exceeds critical threshold (${formatBytes(THRESHOLDS.gpuMemoryCriticalBytes)})`,
        suggestion: 'Review texture sizes, dispose unused geometries/materials, and reduce render target count.',
      });
    } else if (gpu.totalBytes > THRESHOLDS.gpuMemoryBytes) {
      issues.push({
        id: 'high_gpu_memory',
        severity: 'warning',
        category: 'memory',
        message: `GPU memory (${formatBytes(gpu.totalBytes)}) exceeds threshold (${formatBytes(THRESHOLDS.gpuMemoryBytes)})`,
        suggestion: 'Review texture sizes, dispose unused geometries/materials, and reduce render target count.',
      });
    }
  }

  if (gpu?.textures?.sizeBytes !== undefined && gpu.textures.sizeBytes > THRESHOLDS.textureMemoryBytes) {
    issues.push({
      id: 'high_texture_memory',
      severity: 'warning',
      category: 'memory',
      message: `Texture GPU memory (${formatBytes(gpu.textures.sizeBytes)}, ${gpu.textures.count} textures) exceeds threshold (${formatBytes(THRESHOLDS.textureMemoryBytes)})`,
      suggestion: 'Use compressed textures, lower resolution, atlasing, or dispose unused textures.',
    });
  }

  if (gpu?.programs && gpu.programs.count > THRESHOLDS.programCount) {
    issues.push({
      id: 'high_program_count',
      severity: 'info',
      category: 'memory',
      message: `Shader program count (${gpu.programs.count}) exceeds threshold (${THRESHOLDS.programCount})`,
      suggestion: 'Merge materials, reuse shaders, or reduce unique material variants.',
    });
  }

  if (!snapshot.timing?.profiler.enabled) {
    issues.push({
      id: 'profiler_disabled',
      severity: 'info',
      category: 'timing',
      message: 'CPU profiler was not enabled — timing scopes are omitted',
      suggestion: 'Run perf.snapshot 3 or enable the profiler before capture for timing data.',
    });
  }

  return issues;
}

/**
 * Collects a compact performance snapshot from the active world.
 */
export function collectPerformanceSnapshot(
  world: World,
  options: PerformanceSnapshotOptions = {},
): PerformanceSnapshotV1 {
  const actors = world.getActors();
  const renderer = world.getRenderer();

  const render: PerformanceSnapshotRender = renderer
    ? readRenderStats(renderer)
    : {
      drawCalls: 0,
      triangles: 0,
      points: 0,
      lines: 0,
      geometries: 0,
      textures: 0,
    };

  const memory: PerformanceSnapshotMemory = {};
  const perfMemory = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
  if (perfMemory) {
    memory.jsHeapUsedMB = Math.round((perfMemory.usedJSHeapSize / (1024 * 1024)) * 100) / 100;
  }
  if (renderer) {
    memory.gpu = readGpuMemoryStats(renderer);
  }

  const profilerEnabled = THREE.ProfilerService.isEnabled();
  let timing: PerformanceSnapshotTiming | undefined;
  if (profilerEnabled) {
    const { scopes, frameStats } = splitProfilerStats();
    const frameTimeMs = estimateFrameTimeMs(scopes, frameStats);
    timing = {
      profiler: {
        enabled: true,
        profile: THREE.ProfilerService.getProfile(),
        sampleWindow: PROFILER_SAMPLE_WINDOW,
      },
      frameStats,
      scopes,
      frameTimeMs,
    };
  }

  const physicsEngine = world.gameLoop?.physicsEngine;
  const simulation: PerformanceSnapshotSimulation = {
    physics: physicsEngine?.getPerformanceStats(),
    timers: { activeTimers: world.timerSystem.getActiveTimerCount() },
    tweens: { activeTweens: world.tweenManager.getActiveTweenCount() },
    tickAudit: collectTickAudit(actors),
    idleTickerCount: actors.filter(isIdleTicker).length,
  };

  const partial: Omit<PerformanceSnapshotV1, 'issues'> = {
    schemaVersion: SCHEMA_VERSION,
    capturedAt: new Date().toISOString(),
    meta: {
      gameId: options.gameId,
      mapName: options.mapName,
      renderer: renderer?.asWebGL() ? 'webgl' : renderer?.asWebGPU() ? 'webgpu' : 'unknown',
      playState: world.getPlayState(),
      fps: options.fps ?? (timing?.frameTimeMs ? Math.round(1000 / timing.frameTimeMs) : undefined),
    },
    render,
    world: {
      actorCount: actors.length,
      actorsByClass: collectActorsByClass(actors),
    },
    memory,
    timing,
    simulation,
    network: collectNetworkSnapshot(world),
  };

  return {
    ...partial,
    issues: buildIssues(partial),
  };
}

/**
 * Formats a short text summary for console output.
 */
export function formatPerformanceSnapshotSummary(snapshot: PerformanceSnapshotV1): string {
  const lines: string[] = [
    `[PerformanceSnapshot] captured ${snapshot.capturedAt}`,
    `  renderer: ${snapshot.meta.renderer} | play: ${snapshot.meta.playState}${snapshot.meta.fps ? ` | fps: ~${snapshot.meta.fps}` : ''}`,
    `  render: ${snapshot.render.drawCalls} draws, ${snapshot.render.triangles} tris, ${snapshot.render.geometries} geo, ${snapshot.render.textures} tex`,
    `  world: ${snapshot.world.actorCount} actors`,
  ];

  if (snapshot.memory.gpu) {
    const gpu = snapshot.memory.gpu;
    const parts: string[] = [];
    if (gpu.totalBytes !== undefined) {
      parts.push(`${formatBytes(gpu.totalBytes)} total`);
    }
    if (gpu.textures.sizeBytes !== undefined) {
      parts.push(`tex ${formatBytes(gpu.textures.sizeBytes)} (${gpu.textures.count})`);
    } else if (gpu.textures.count > 0) {
      parts.push(`${gpu.textures.count} tex`);
    }
    if (gpu.attributes?.sizeBytes !== undefined) {
      parts.push(`attrs ${formatBytes(gpu.attributes.sizeBytes)} (${gpu.attributes.count})`);
    }
    if (parts.length > 0) {
      lines.push(`  gpu memory: ${parts.join(' | ')}`);
    }
  }
  if (snapshot.memory.jsHeapUsedMB !== undefined) {
    lines.push(`  js heap: ${snapshot.memory.jsHeapUsedMB} MB`);
  }

  if (snapshot.simulation?.physics) {
    const p = snapshot.simulation.physics;
    const collisionPart = p.collisionEventsLastFrame !== undefined
      ? `, ${p.collisionEventsLastFrame} collision events/frame`
      : '';
    lines.push(`  physics: ${p.bodies} bodies, ${p.colliders} colliders, ${p.vehicles} vehicles${collisionPart}`);
  }
  if (snapshot.simulation?.idleTickerCount !== undefined) {
    lines.push(`  simulation: ${snapshot.simulation.idleTickerCount} idle tickers, ${snapshot.simulation.timers?.activeTimers ?? 0} timers, ${snapshot.simulation.tweens?.activeTweens ?? 0} tweens`);
  }

  if (snapshot.timing?.frameStats) {
    const fs = snapshot.timing.frameStats;
    lines.push(`  frame timing (${fs.frameCount} samples): avg ${fs.avgMs.toFixed(1)} ms | p95 ${fs.p95Ms.toFixed(1)} ms | max ${fs.maxMs.toFixed(1)} ms | slow frames: ${fs.slowFrames}/${fs.frameCount}`);
  }

  if (snapshot.timing?.scopes.length) {
    const hasSelf = snapshot.timing.scopes.some(s => s.selfAvg !== undefined);
    const hasCpf = snapshot.timing.scopes.some(s => s.callsPerFrame !== undefined);
    const header = hasSelf
      ? '  top function scopes — incl avg | incl p95 | excl avg | excl p95 (ms):'
      : '  top function scopes (avg ms):';
    lines.push(header);
    for (const scope of snapshot.timing.scopes.slice(0, 10)) {
      const cpfPart = hasCpf
        ? `  ×${(scope.callsPerFrame ?? 0).toFixed(1)}/fr`
        : '';
      if (hasSelf && scope.selfAvg !== undefined) {
        lines.push(
          `    ${scope.label.padEnd(48)} incl: ${scope.avg.toFixed(2).padStart(7)} / p95 ${scope.p95.toFixed(2).padStart(7)}  excl: ${scope.selfAvg.toFixed(2).padStart(7)} / p95 ${scope.selfP95!.toFixed(2).padStart(7)}${cpfPart}`,
        );
      } else {
        lines.push(`    ${scope.label.padEnd(48)} avg: ${scope.avg.toFixed(2).padStart(7)} ms  p95: ${scope.p95.toFixed(2).padStart(7)} ms${cpfPart}`);
      }
    }
  } else if (snapshot.timing?.profiler.enabled) {
    lines.push('  no function scopes recorded — ensure profiler is enabled BEFORE game starts, or run: profiler.enable');
  }

  if (snapshot.issues.length) {
    lines.push(`  issues (${snapshot.issues.length}):`);
    for (const issue of snapshot.issues.slice(0, 8)) {
      lines.push(`    [${issue.severity}] ${issue.message}`);
    }
  }

  return lines.join('\n');
}

function buildSnapshotFilename(): string {
  const now = new Date();
  const p = (n: number) => `${n}`.padStart(2, '0');
  const stamp = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}_${p(now.getHours())}-${p(now.getMinutes())}-${p(now.getSeconds())}`;
  return `gnsx-perf-${stamp}.json`;
}

/**
 * Downloads a performance snapshot as JSON in the browser.
 */
export function downloadPerformanceSnapshot(snapshot: PerformanceSnapshotV1, filename?: string): void {
  if (typeof document === 'undefined') {
    console.log('[PerformanceSnapshot] downloadPerformanceSnapshot() only works in the browser.');
    console.log(JSON.stringify(snapshot, null, 2));
    return;
  }

  const json = JSON.stringify(snapshot, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? buildSnapshotFilename();
  a.click();
  URL.revokeObjectURL(url);
  console.log(`[PerformanceSnapshot] Downloaded: ${a.download} (${json.length} bytes, ${snapshot.issues.length} issues)`);
}
